/**
 * ForgotPassword.jsx
 * Sends a password reset email via Supabase.
 *
 * Add to App.jsx:
 *   import ForgotPassword from "./pages/ForgotPassword";
 *   <Route path="/forgot-password" element={<ForgotPassword />} />
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { ArrowLeft } from "lucide-react";

export default function ForgotPassword() {
  const [email,   setEmail]   = useState("");
  const [loading, setLoading] = useState(false);
  const [sent,    setSent]    = useState(false);
  const [error,   setError]   = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { setError("Please enter your email address."); return; }
    setLoading(true);
    setError("");
    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (resetError) throw resetError;
      setSent(true);
    } catch (err) {
      setError(err.message || "Failed to send reset email. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: "100vh", background: "#0a0b0f",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontFamily: "sans-serif", padding: 24,
    }}>
      <div style={{ width: "100%", maxWidth: 420 }}>

        <Link to="/login" style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          color: "#64748b", fontSize: 13, marginBottom: 28,
          textDecoration: "none",
        }}>
          <ArrowLeft size={15} /> Back to Login
        </Link>

        {/* Brand */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 28 }}>
          <div style={{
            width: 44, height: 44, borderRadius: 11,
            background: "linear-gradient(135deg,#6366f1,#8b5cf6)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 20, fontWeight: 900, color: "#fff",
          }}>N</div>
          <div>
            <h1 style={{ fontSize: 20, fontWeight: 800, color: "#f1f5f9", margin: 0 }}>
              Forgot Password
            </h1>
            <p style={{ fontSize: 12, color: "#64748b", margin: 0 }}>
              We'll send a reset link to your email
            </p>
          </div>
        </div>

        {!sent ? (
          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#94a3b8", marginBottom: 6 }}>
                Email Address
              </label>
              <input
                id="email" name="email" type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                style={{
                  width: "100%", padding: "10px 14px", borderRadius: 9,
                  border: "1px solid #2a2d3a", background: "#1a1d26",
                  color: "#f1f5f9", fontSize: 13, outline: "none",
                  boxSizing: "border-box",
                }}
              />
            </div>

            {error && (
              <div style={{
                background: "rgba(248,113,113,0.1)", border: "1px solid rgba(248,113,113,0.2)",
                borderRadius: 8, padding: "10px 14px",
                fontSize: 12, color: "#f87171",
              }}>{error}</div>
            )}

            <button
              type="submit"
              disabled={loading}
              style={{
                padding: "12px", borderRadius: 10, border: "none",
                background: loading ? "#374151" : "#6366f1",
                color: "#fff", fontSize: 14, fontWeight: 700,
                cursor: loading ? "not-allowed" : "pointer",
              }}
            >
              {loading ? "Sending…" : "Send Reset Link"}
            </button>
          </form>
        ) : (
          /* Success state */
          <div style={{
            background: "#12141a", border: "1px solid rgba(74,222,128,0.2)",
            borderRadius: 12, padding: "32px 24px", textAlign: "center",
          }}>
            <div style={{
              width: 56, height: 56, borderRadius: 14,
              background: "rgba(74,222,128,0.1)", border: "1px solid rgba(74,222,128,0.2)",
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 16px", fontSize: 24,
            }}>✉️</div>
            <h2 style={{ fontSize: 18, fontWeight: 800, color: "#f1f5f9", margin: "0 0 8px" }}>
              Check your email
            </h2>
            <p style={{ fontSize: 13, color: "#64748b", margin: "0 0 6px", lineHeight: 1.7 }}>
              We sent a password reset link to
            </p>
            <p style={{ fontSize: 13, fontWeight: 700, color: "#818cf8", margin: "0 0 24px" }}>
              {email}
            </p>
            <p style={{ fontSize: 11, color: "#374151", margin: "0 0 20px", lineHeight: 1.6 }}>
              Click the link in the email to reset your password. The link expires in 1 hour.
            </p>
            <button
              onClick={() => { setSent(false); setEmail(""); }}
              style={{
                padding: "8px 20px", borderRadius: 8,
                border: "1px solid #2a2d3a", background: "transparent",
                color: "#94a3b8", fontSize: 12, fontWeight: 600, cursor: "pointer",
              }}
            >
              Try a different email
            </button>
          </div>
        )}

        <p style={{ textAlign: "center", fontSize: 12, color: "#64748b", marginTop: 20 }}>
          Remember your password?{" "}
          <Link to="/login" style={{ color: "#818cf8", fontWeight: 600, textDecoration: "none" }}>
            Sign In
          </Link>
        </p>
      </div>
    </div>
  );
}
